import {
  Alert,
  Button,
  Select,
  SelectOptionElement,
} from '@odin-labs/components';
import React from 'react';

export type RevisionBanner = {
  text: string;
  changes: string[];
  currentLabel: string;
  onViewCurrent: () => void;
  canStartDraft: boolean;
  onStartDraft: () => void;
};

export type LayoutHeaderProps = {
  revisionOptions: SelectOptionElement[];
  revisionValue: SelectOptionElement | null;
  onRevisionChange: (value: string) => void;
  isDraft: boolean;
  editingDraft: boolean;
  showEdit: boolean;
  /** A command is running: opening a draft waits for it, like the Add buttons. */
  busy: boolean;
  /** A command was given up on: the page must be reloaded before any other change. */
  stalled: boolean;
  onEditLayout: () => void;
  onOpenDiscard: () => void;
  onOpenPublish: () => void;
  banner: RevisionBanner | null;
};

const classes = {
  root: 'odin-flex odin-flex-shrink-0 odin-flex-col odin-gap-3',
  row: 'odin-flex odin-flex-wrap odin-items-center odin-justify-between odin-gap-3',
  left: 'odin-flex odin-min-w-0 odin-items-center odin-gap-3',
  select: 'odin-w-72',
  // The alert stretches to the row, so it stands exactly as tall as the
  // revision select; its own vertical padding would push it taller.
  notice:
    'odin-flex odin-min-w-0 odin-flex-1 odin-self-stretch [&>div]:odin-flex-1 [&>div>div:first-child]:odin-py-0',
  actions: 'odin-flex odin-flex-shrink-0 odin-items-center odin-gap-2',
  revisionBanner:
    'odin-flex odin-flex-wrap odin-items-center odin-justify-between odin-gap-3 odin-rounded-md odin-border odin-border-gray-200 odin-bg-gray-50 odin-px-4 odin-py-2.5',
  bannerText: 'odin-flex odin-flex-col odin-gap-1',
  bannerMain: 'odin-text-sm odin-text-gray-900',
  bannerChange: 'odin-text-xs odin-text-gray-500',
  bannerActions: 'odin-flex odin-gap-2',
};

/** The revision being looked at, the draft notice, and the draft's controls. */
export function LayoutHeader({
  revisionOptions,
  revisionValue,
  onRevisionChange,
  isDraft,
  editingDraft,
  showEdit,
  busy,
  stalled,
  onEditLayout,
  onOpenDiscard,
  onOpenPublish,
  banner,
}: LayoutHeaderProps): React.ReactElement {
  const blocked = busy || stalled;
  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <div className={classes.left}>
          <div className={classes.select}>
            <Select
              name="revision"
              value={revisionValue}
              options={revisionOptions}
              isSearchable={false}
              isClearable={false}
              onChange={(option: SelectOptionElement | null) => {
                if (option) onRevisionChange(option.value);
              }}
            />
          </div>
        </div>
        {stalled && (
          <div className={classes.notice}>
            <Alert
              type="warn"
              text="A change is still waiting on the server. Refresh the page before making more changes."
            />
          </div>
        )}
        {editingDraft && !stalled && (
          <div className={classes.notice}>
            <Alert
              type="info"
              text="You are editing a draft. Nothing changes on site until you publish."
            />
          </div>
        )}
        <div className={classes.actions}>
          {showEdit && (
            <Button
              text="Edit layout"
              size="sm"
              disabled={blocked}
              onClick={onEditLayout}
            />
          )}
          {isDraft && (
            <>
              <Button
                text="Discard draft"
                size="sm"
                theme="white"
                disabled={blocked}
                onClick={onOpenDiscard}
              />
              <Button
                text="Publish"
                size="sm"
                disabled={blocked}
                onClick={onOpenPublish}
              />
            </>
          )}
        </div>
      </div>
      {banner && (
        <div className={classes.revisionBanner}>
          <div className={classes.bannerText}>
            <span className={classes.bannerMain}>{banner.text}</span>
            {banner.changes.map((change) => (
              <span key={change} className={classes.bannerChange}>
                {change}
              </span>
            ))}
          </div>
          <div className={classes.bannerActions}>
            <Button
              text={banner.currentLabel}
              size="xs"
              theme="white"
              onClick={banner.onViewCurrent}
            />
            {banner.canStartDraft && (
              <Button
                text="Start a draft from this revision"
                size="xs"
                disabled={blocked}
                onClick={banner.onStartDraft}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
